// S-04 Protection: allowed embed origins, validated client-side (Frontend §7.2, §8.1 "S-04 Protection origin
// validation"; Security §3.8, §6.5). The server checks the same rule (`origin_invalid`); the build script too (D-59).

/** At most this many origins per clinic (Security §3.8, D-57). */
export const MAX_ORIGINS = 10

export type OriginProblem =
  'required' | 'not_https' | 'has_path' | 'wildcard' | 'not_lowercase' | 'invalid' | 'duplicate'

/** Field messages (our copy: UserFlows gives none beyond the rule itself). */
export const ORIGIN_MESSAGES: Record<OriginProblem, string> = {
  required: 'Enter a website address, or remove the row.',
  not_https: 'Use an https:// address.',
  has_path: 'Use the site’s address only: no path, query or trailing slash.',
  wildcard: 'Wildcards aren’t allowed: list each site.',
  not_lowercase: 'Use lowercase letters.',
  invalid: 'Enter an address like https://www.clinic.example',
  duplicate: 'This address is already in the list.',
}

/**
 * The problem with one origin, or null: exactly `https://host[:port]`, lowercase, no path, query, fragment,
 * credentials or wildcard.
 */
export function originProblem(raw: string): OriginProblem | null {
  const value = raw.trim()
  if (value === '') return 'required'
  if (value.includes('*')) return 'wildcard'
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return 'invalid'
  if (!/^https:\/\//i.test(value)) return 'not_https'
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return 'invalid'
  }
  if (url.username !== '' || url.password !== '') return 'invalid'
  const rest = value.slice('https://'.length)
  if (/[/?#]/.test(rest)) return 'has_path'
  if (value !== value.toLowerCase()) return 'not_lowercase'
  if (!/^https:\/\/[a-z0-9.-]+(:\d{1,5})?$/.test(value) || url.origin !== value) return 'invalid'
  return null
}

/** Every row's problem (index → problem), duplicates included; empty when the list is valid. */
export function originProblems(origins: readonly string[]): Map<number, OriginProblem> {
  const out = new Map<number, OriginProblem>()
  const seen = new Set<string>()
  origins.forEach((raw, i) => {
    const problem = originProblem(raw)
    const value = raw.trim()
    if (problem) out.set(i, problem)
    else if (seen.has(value)) out.set(i, 'duplicate')
    seen.add(value)
  })
  return out
}
